// scrivitoInit must be first – it calls Scrivito.configure before any
// provideObjClass / provideWidgetClass / connect calls run.
import './scrivitoInit';

import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import * as Scrivito from 'scrivito';

// Bootstrap's CSS is compiled from source at the top of styles/main.scss, with
// Bootstrap 3's breakpoints and gutter (see the comment there); the prebuilt
// bootstrap.min.css cannot be reconfigured.
//
// Of Bootstrap's JS components, only the language-switcher dropdown
// (Navigation.jsx's data-bs-toggle="dropdown") is actually used anywhere in
// this app - the services accordion and mobile nav toggle are both plain
// React state. bootstrap.bundle.min.js pulls in every component (modal,
// carousel, tooltip, popover, scrollspy, tab, toast...) for that one dropdown;
// importing just the component halves the JS this app ships (confirmed via
// Lighthouse: was contributing to a 648KB/197KB-gzipped bundle).
import 'bootstrap/js/dist/dropdown';

// Webfonts are self-hosted, as in the Rails app – see styles/_fonts.scss, which
// serves its very font files. Loading them from fonts.googleapis.com would
// transmit visitor IPs to Google before cookie consent is given, a GDPR exposure
// German courts have ruled on.
import './styles/main.scss';

// Register all Obj and Widget classes
import './Objs/index';
import './Widgets/index';

import App from './App';

// The static meta/OG tags in index.html are a no-JS fallback (see SeoHead) –
// react-helmet-async has no idea they exist and only ever adds its own, so
// leaving them in place would double up every description/og: tag once a
// page mounts.
document.querySelectorAll('[data-fallback="true"]').forEach((el) => el.remove());

// api/prerender.mjs's loader sets window.preloadDump on pages it prerendered
// (see src/entry-server.jsx): a Promise for the dump's text, fetched from
// dist/preload-dumps/ alongside this bundle (null if that fetch failed).
// Scrivito.preload() feeds it into the SDK's cache so the first render
// already has real data instead of the cold fetch that caused the 404 flash
// fixed earlier. It resolves dumpLoaded: false itself when
// Scrivito.isEditorLoggedIn(), or when visitor authentication is enabled
// (documented at docs.scrivito.com/js-sdk/preload) - in every one of those
// cases, and with no dump at all, this must fall back to a plain client
// render.
if (window.preloadDump) {
  Promise.resolve(window.preloadDump)
    .then((dump) => (dump ? Scrivito.preload(dump) : { dumpLoaded: false }))
    .catch(() => ({ dumpLoaded: false }))
    .then(({ dumpLoaded }) => {
      delete window.preloadDump;
      if (dumpLoaded) {
        hydrateRoot(document.getElementById('root'), <App />);
        // The dump is whatever was published when api/prerender.mjs last ran
        // (the last deploy, or the last publish-webhook call - see
        // api/routes/webhooks.js) - it can already be stale by the time a
        // visitor loads the page. This is Scrivito's own documented pattern
        // (docs.scrivito.com/js-sdk/updateContent: "ReactDOM.hydrate(el,
        // container, () => Scrivito.updateContent())") for exactly that: it
        // diffs the preloaded content state against what's actually published
        // right now and patches in whatever changed, so a real visitor never
        // waits on a redeploy to see a just-published edit.
        Scrivito.updateContent();
      } else {
        renderApp();
      }
    });
} else {
  renderApp();
}

function renderApp() {
  createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}
